import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Form, Row, Select, Tooltip, Tour } from "antd";
import { VideoCameraOutlined } from "@ant-design/icons";
import FormItem from "antd/es/form/FormItem";
import FilterMap from "../../FilterMap";
import tutorialVideo from "../../../videos/explore_practices.mp4";
import "../style.css";
import { useDispatch, useSelector } from "react-redux";
import { nextStep, setActiveDropdown } from "../../../Redux/tutorialSlice";
import tutorialSteps from "../../Tutorial/tutorialSteps";
import useCutout from "../../Tutorial/useCutout";
import {
  setSelectedCounty,
  setSelectedFips,
  setSelectedState,
} from "../../../Redux/filterSlice";
import { fetchGeoCountiesByState } from "../../../Redux/geo/geoThunk";
import { fetchAllPracticeDataByCounty } from "../../../Redux/practice/practiceThunk";

const Step1 = () => {
  const inputRef = useRef(null);
  const [form] = Form.useForm();
  const [startTour, setStartTour] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useDispatch();
  const { updateCutout } = useCutout();

  const { activeDropdown, currentStep, isTutorialActive } = useSelector(
    (state) => state.tutorial
  );
  const { selectedState, selectedCounty } = useSelector(
    (state) => state.filter
  );
  const { stateList, countyList, countyListData } = useSelector(
    (state) => state.geo
  );

  // ---------- Device helpers ----------
  function useIsDesktop() {
    const [isDesktop, setIsDesktop] = useState(
      typeof window !== "undefined" ? window.innerWidth >= 992 : true
    );
    useEffect(() => {
      const onResize = () => setIsDesktop(window.innerWidth >= 992);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, []);
    return isDesktop;
  }
  const isDesktop = useIsDesktop();
  const allowSearch = isDesktop && !isTutorialActive;

  const isTouchDevice = () =>
    typeof window !== "undefined" &&
    (window.matchMedia?.("(hover: none)").matches || "ontouchstart" in window);

  // ---------- Background scroll lock (safe, tutorial-friendly) ----------
  const openDropdownsRef = useRef(0);
  const savedScrollYRef = useRef(0);

  const lockBackgroundScroll = () => {
    // don’t interfere with the tutorial’s own scroll management
    if (isTutorialActive) return;

    // (Optionally) only lock on touch devices
    if (!isTouchDevice()) return;

    openDropdownsRef.current += 1;
    if (openDropdownsRef.current > 1) return; // already locked

    savedScrollYRef.current =
      window.scrollY || document.documentElement.scrollTop || 0;

    document.body.style.top = `-${savedScrollYRef.current}px`;
    document.body.classList.add("bg-scroll-locked");
  };

  const unlockBackgroundScroll = () => {
    // only unlock when all dropdowns are closed
    openDropdownsRef.current = Math.max(0, openDropdownsRef.current - 1);
    if (openDropdownsRef.current !== 0) return;

    if (document.body.classList.contains("bg-scroll-locked")) {
      document.body.classList.remove("bg-scroll-locked");
      const y = savedScrollYRef.current || 0;
      document.body.style.top = "";
      window.scrollTo(0, y);
    }
  };

  // Safety: ensure unlock on unmount if a dropdown was open
  useEffect(() => {
    return () => {
      while (openDropdownsRef.current > 0) {
        unlockBackgroundScroll();
      }
    };
    // intentionally no deps (cleanup on unmount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Dropdown open/close handling (and tutorial cutout sync) ----------
  const handleDropdownOpen = (open, selector) => {
    setIsOpen(open);

    if (open) lockBackgroundScroll();
    else unlockBackgroundScroll();

    if (isTutorialActive) {
      if (open) {
        dispatch(setActiveDropdown(selector));
        updateCutout(selector, "custom", {
          isOpen: true,
          manageLoading: false,
        });
      } else {
        dispatch(setActiveDropdown(null));
        updateCutout(selector, "custom", {
          isOpen: false,
          manageLoading: false,
        });
      }
    }
  };

  // ---------- Tutorial cutout lifecycle ----------
  useEffect(() => {
    if (!isTutorialActive) return;

    const step = tutorialSteps[currentStep];
    if (!step || !step.target) return;

    updateCutout(step.target, step.advanceType, {
      isOpen: false,
      manageLoading: true,
    });
  }, [currentStep, isTutorialActive, updateCutout]);

  useEffect(() => {
    if (!isTutorialActive) return;

    if (activeDropdown && !selectedState) {
      updateCutout(activeDropdown, "custom", {
        isOpen: true,
        manageLoading: false,
      });
    } else if (!selectedState) {
      updateCutout(tutorialSteps[currentStep]?.target, "custom", {
        isOpen: false,
        manageLoading: true,
      });
    }
  }, [
    activeDropdown,
    currentStep,
    isTutorialActive,
    selectedState,
    updateCutout,
  ]);

  // ---------- Select handlers ----------
  const handleStateChange = (value) => {
    dispatch(setSelectedState(value));
    dispatch(setSelectedCounty(null));
    dispatch(fetchGeoCountiesByState(value));

    if (isTutorialActive) {
      dispatch(nextStep());
    }
  };

  const handleCountyChange = (value) => {
    dispatch(setSelectedCounty(value));

    const matchedCounty = countyListData.find(
      (county) => county.cname === value
    );

    if (matchedCounty?.fips) {
      dispatch(setSelectedFips(String(matchedCounty.fips).padStart(5, "0")));
    } else {
      dispatch(setSelectedFips(null));
    }

    if (selectedState) {
      dispatch(
        fetchAllPracticeDataByCounty({
          state: selectedState,
          county: value,
        })
      );
    }

    if (isTutorialActive) {
      dispatch(nextStep());
    }
  };

  return (
    <>
      <Tooltip title="Watch the video tutorial">
        <Button
          type="primary"
          onClick={() => setStartTour(true)}
          style={{ float: "right" }}
        >
          <VideoCameraOutlined />
        </Button>
      </Tooltip>
      <p>
        <br />
        <b>
          1) Available practices and practice standards vary by state. Please
          select your state and county to begin.
        </b>
        <br />
        <br />
      </p>

      <Form className="filter-form" ref={inputRef} form={form}>
        <div className="state-county-div">
          <div className="state-dropdown">
            <Form.Item label="State">
              <Select
                className="select-font-size state-sel-tutorial"
                showSearch={allowSearch}
                defaultValue={selectedState}
                placeholder="Select State"
                onChange={handleStateChange}
                options={stateList}
                value={selectedState}
                onDropdownVisibleChange={(open) =>
                  handleDropdownOpen(open, ".state-sel-tutorial")
                }
              />
            </Form.Item>
          </div>

          <div className="county-dropdown">
            <FormItem label="County">
              <Select
                className="select-font-size county-sel-tutorial"
                showSearch={allowSearch}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                placeholder="Select County"
                defaultValue={selectedCounty}
                onChange={handleCountyChange}
                options={countyList}
                value={selectedCounty}
                onDropdownVisibleChange={(open) =>
                  handleDropdownOpen(open, ".county-sel-tutorial")
                }
              />
            </FormItem>
          </div>
        </div>

        <div className="filter-map">
          <br />
          <FilterMap />
        </div>
      </Form>

      <Tour
        open={startTour}
        onClose={() => setStartTour(false)}
        steps={[
          {
            title: "Video Tutorial",
            description: "Please follow the steps in this video tutorial",
            cover: (
              <video width="400px" height="300px" controls>
                <source src={tutorialVideo} type="video/mp4" />
              </video>
            ),
            target: () => null,
          },
        ]}
      />
    </>
  );
};

export default Step1;
